import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';

const DonorProfile = () => {
  const navigate = useNavigate();
  
  const [user, setUser] = useState<any>(null);
  const [donations, setDonations] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const storedUser = localStorage.getItem('sevaa_user');
    if (storedUser) {
      const parsedUser = JSON.parse(storedUser);
      const userEmail = parsedUser.email;

      // Keep user in state for receipt generation
      setUser(parsedUser);

      // Donation History Fetch
      fetch(`https://www.sevaafoundation.com/seva-backend/api/donations/user_history.php?email=${userEmail}`)
        .then(res => res.json())
        .then(data => {
          if (data.success) {
            setDonations(data.donations);
          }
          setIsLoading(false);
        })
        .catch(err => {
          console.error("History fetch error:", err);
          setIsLoading(false);
        });

    } else {
      navigate('/');
    }
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('sevaa_user');
    navigate('/');
  };

  const downloadReceipt = (donation: any) => {
    const receiptHTML = `
      <html>
      <head>
        <title>Receipt - ${donation.receipt_no || donation.transaction_id}</title>
        <style>
          body { font-family: 'Arial', sans-serif; padding: 40px; color: #333; }
          .receipt-box { max-width: 800px; margin: auto; padding: 30px; border: 1px solid #eee; border-top: 5px solid #0f766e; box-shadow: 0 0 10px rgba(0,0,0,0.05); }
          .header { display: flex; justify-content: space-between; align-items: center; border-bottom: 2px solid #eee; padding-bottom: 20px; margin-bottom: 20px; }
          .title { font-size: 24px; font-weight: bold; color: #0f766e; margin: 0; }
          .subtitle { font-size: 12px; color: #666; margin-top: 5px; }
          .details-grid { display: flex; justify-content: space-between; margin-bottom: 30px; }
          .details-col { width: 48%; }
          .label { font-size: 11px; color: #888; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 3px; }
          .value { font-size: 14px; font-weight: bold; margin-bottom: 15px; }
          .amount-box { background: #f8f9fa; padding: 20px; text-align: center; border-radius: 8px; margin-bottom: 30px; }
          .amount { font-size: 32px; font-weight: bold; color: #0f766e; margin: 0; }
          .footer { text-align: center; font-size: 12px; color: #888; border-top: 1px solid #eee; padding-top: 20px; }
        </style>
      </head>
      <body>
        <div class="receipt-box">
          <div class="header">
            <div>
              <h1 class="title">Sevaa Foundation</h1>
              <p class="subtitle">Swadesh Sevaa Charitable Foundation (Govt. Registered NGO)</p>
            </div>
            <div style="text-align: right;">
              <h2 style="margin: 0; color: #ffb000;">DONATION RECEIPT</h2>
              <p class="subtitle">Date: ${donation.date}</p>
            </div>
          </div>
          
          <div class="amount-box">
            <p class="label">Amount Donated</p>
            <p class="amount">₹${parseFloat(donation.amount).toFixed(2)}</p>
            <p style="margin-top: 10px; font-weight: bold;">Status: <span style="color: ${donation.payment_status === 'success' ? 'green' : 'red'}; text-transform: uppercase;">${donation.payment_status}</span></p>
          </div>

          <div class="details-grid">
            <div class="details-col">
              <p class="label">Donor Name</p>
              <p class="value">${donation.name || user.name}</p>
              
              <p class="label">Email Address</p>
              <p class="value">${donation.email || user.email}</p>
              
              <p class="label">PAN Number</p>
              <p class="value">${donation.pan || 'Not Provided'}</p>
            </div>
            <div class="details-col">
              <p class="label">Receipt Number</p>
              <p class="value">${donation.receipt_no || 'N/A'}</p>
              
              <p class="label">Transaction ID</p>
              <p class="value">${donation.transaction_id || 'N/A'}</p>
              
              <p class="label">Campaign / Cause</p>
              <p class="value">${donation.campaign_name || 'General Donation (Where needed most)'}</p>
            </div>
          </div>

          <div class="footer">
            <p><strong>Thank you for your generous contribution!</strong></p>
            <p>All donations are eligible for tax exemption under Section 80G of the Income Tax Act.</p>
            <p>This is a computer-generated receipt and does not require a physical signature.</p>
          </div>
        </div>
        <script>
          window.onload = function() { window.print(); }
        </script>
      </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(receiptHTML);
      printWindow.document.close();
    } else {
      alert("Please allow pop-ups to download the receipt.");
    }
  };

  if (isLoading || !user) {
    return <div className="min-h-screen flex items-center justify-center font-poppins text-[#0f766e] font-black tracking-widest uppercase">Loading Dashboard...</div>;
  }

  return (
    <>
      <Helmet>
        <title>Donor Dashboard | SEVAA Foundation</title>
        <meta name="robots" content="noindex, nofollow" />
        <meta name="description" content="Private SEVAA Foundation donation history." />
      </Helmet>
      <div className="min-h-screen bg-slate-50 font-poppins py-10 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto">
        
        <div className="mb-8">
          <h1 className="text-3xl font-black text-slate-900 tracking-wider uppercase">My Dashboard</h1>
          <p className="text-sm font-bold text-slate-500 mt-1 uppercase tracking-widest">Manage your contributions</p>
        </div>

        <div className="flex flex-col md:flex-row gap-8">
          
          {/* LEFT SIDEBAR - Cleaned up to hide personal details */}
          <div className="w-full md:w-64 shrink-0">
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden sticky top-24">
              <div className="p-6 bg-[#0f766e] text-center">
                <div className="w-16 h-16 bg-white/20 text-white rounded-full flex items-center justify-center mx-auto text-2xl font-black uppercase mb-3">
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
                  </svg>
                </div>
                <h2 className="text-lg font-black text-white tracking-wider uppercase">Donor Portal</h2>
              </div>

              <div className="flex flex-col py-2">
                <div className="px-6 py-4 text-xs font-black uppercase tracking-widest flex items-center gap-3 border-l-4 border-[#ffb000] text-[#0f766e] bg-orange-50/50">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                  </svg>
                  Donation History
                </div>

                <div className="px-6 py-4 mt-2 border-t border-slate-100">
                  <button onClick={handleLogout} className="w-full flex items-center gap-3 text-xs font-black uppercase tracking-widest text-red-500 hover:text-red-700 transition-colors text-left">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                    Logout Account
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT CONTENT AREA - Only showing History */}
          <div className="flex-1">
            <motion.div 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 md:p-8"
            >
              <div className="mb-6 border-b border-slate-100 pb-4">
                <h3 className="text-xl font-black text-slate-800 uppercase tracking-widest">Donation History</h3>
                <p className="text-xs font-bold text-slate-500 mt-1">Your past contributions to Sevaa Foundation</p>
              </div>

              {donations.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="bg-slate-50 text-[10px] font-black text-slate-500 uppercase tracking-widest border-b border-slate-200">
                        <th className="p-4 rounded-tl-xl whitespace-nowrap">Date</th>
                        <th className="p-4 whitespace-nowrap">Txn ID / Receipt</th>
                        <th className="p-4 whitespace-nowrap">Campaign</th>
                        <th className="p-4 whitespace-nowrap">Amount</th>
                        <th className="p-4 whitespace-nowrap">Status</th>
                        <th className="p-4 rounded-tr-xl text-center whitespace-nowrap">Action</th>
                      </tr>
                    </thead>
                    <tbody className="text-sm font-bold text-slate-700">
                      {donations.map((item, index) => (
                        <tr key={index} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                          <td className="p-4 whitespace-nowrap text-xs">{item.date}</td>
                          <td className="p-4 whitespace-nowrap text-[#0f766e] text-xs">{item.receipt_no || item.transaction_id}</td>
                          <td className="p-4 text-xs text-slate-600 min-w-[150px]">
                            {item.campaign_name || 'General Donation'}
                          </td>
                          <td className="p-4 whitespace-nowrap font-black">₹{parseFloat(item.amount).toFixed(2)}</td>
                          <td className="p-4 whitespace-nowrap">
                            <span className={`px-2 py-1 rounded text-[10px] font-black uppercase tracking-widest ${item.payment_status === 'success' ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'}`}>
                              {item.payment_status}
                            </span>
                          </td>
                          <td className="p-4 whitespace-nowrap text-center">
                            {item.payment_status === 'success' ? (
                              <button 
                                onClick={() => downloadReceipt(item)}
                                className="text-[#ffb000] hover:text-[#e6a000] transition-colors p-1"
                                title="Download Receipt"
                              >
                                <svg className="w-5 h-5 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                              </button>
                            ) : (
                              <span className="text-[10px] text-slate-400">N/A</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-16 bg-slate-50 rounded-xl border border-slate-100 border-dashed">
                  <div className="w-16 h-16 bg-white shadow-sm rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-[#ffb000]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" /></svg>
                  </div>
                  <h4 className="text-base font-black text-slate-800 uppercase tracking-wider">No Donations Yet</h4>
                  <p className="text-xs font-bold text-slate-500 mt-2">You haven't made any donations using this account.</p>
                  <button className="mt-6 bg-[#0f766e] text-white px-6 py-3 rounded-lg text-xs font-black uppercase tracking-widest hover:bg-slate-800 transition-colors">
                    Make a Donation
                  </button>
                </div>
              )}
            </motion.div>
          </div>
        </div>

      </div>
    </div>
    </>
  );
};

export default DonorProfile;